import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { GridComponent, QueryCellInfoEventArgs } from '@syncfusion/ej2-angular-grids';
import { TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { ToastrService } from 'ngx-toastr';
import { Subscription, interval } from 'rxjs';
import { fadeInOut } from '../../../../shared/helpers/animations';
import { IDashboardRequest, IPaginatedResult } from '../../../../shared/models/pagination.model';
import { AuthorizationService, LLDashboardService, StorageManagerService } from '../../../../shared/services';
import { DomSanitizer } from '@angular/platform-browser';
import { DeleteButtonComponent } from '../../../common/delete-button/delete-button.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations/src';
import { Utilities } from '../../../../shared/services/utilities';
import { EsigModalComponent } from '../../../common/esig-modal/esig-modal.component';
import { SettingsService } from '../../../../shared/services/settings.service';

@Component({
  selector: 'app-product-labeling-listing',
  templateUrl: './product-labeling-listing.component.html',
  styleUrls: ['./product-labeling-listing.component.scss'],
  animations: [fadeInOut],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false
})
export class ProductLabelingListingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('LLForm_grid')
  public LLForm_grid: GridComponent;
  @ViewChild('drafts_grid')
  public draftsGrid: GridComponent;
  @ViewChild('deliveries_grid')
  public deliveriesGrid: GridComponent;
  @ViewChild('deleteSPLButton')
  public deleteButton: DeleteButtonComponent;
  @ViewChild('deleteSPLDraftButton')
  public deleteButtonDrafts: DeleteButtonComponent;
  @ViewChild('sendToFDAButton')
  public sendToFDAButton: EsigModalComponent;
  @ViewChildren(NgbNav) viewChildren: QueryList<NgbNav>;
  public tabset: NgbNav;

  @ViewChild('tooltip')
  public tooltip: TooltipComponent;

  // Server-side pagination for LL forms grid
  LLForm_grid_data: any = { result: [], count: 0 };
  public llFormsPageSettings: Object;
  llFormsRequest: IDashboardRequest;
  loadingLLForms = true;

  // Server-side pagination for LL Merge deliveries grid
  llDeliveries_grid_data: any = { result: [], count: 0 };
  public llDeliveriesPageSettings: Object;
  llDeliveriesRequest: IDashboardRequest;
  loadingDeliveries = true;

  public initialPage: Object;
  public sortOptions: Object;
  public formatOptions: any;
  public toolbarOptions: Object;
  count_LLForm: number = 0;
  loading = false;
  submitted = false;
  public searchForm: UntypedFormGroup;
  errorMessage: string;
  bHasPermission = false;
  hasDeletePermission = false;
  dashboardDeliveriesData = [];
  formData: any;
  sub: Subscription;
  activeTab = 'LLFormGrid'; // default
  selectedLLRevisionId: number | undefined;
  selectedLLOrderId: number | undefined;
  selectedLLRevisionDraftMode: boolean | undefined;
  selectedLLRevisionProductType: string | undefined;
  selectedLLRevisionFileName: string | undefined;
  selectedLLRevisionVersion: string | undefined;

  // Selection persistence across paging/sorting/filtering
  private selectedLLFormRevisionId: number | null = null;

  needsToPoll: boolean = true;

  public clipmodecolumn = { class: 'e-tooltipcolumn' };

  constructor(private router: Router, private route: ActivatedRoute, private storageManagerService: StorageManagerService,
    private fb: UntypedFormBuilder, private authorizationService: AuthorizationService, private toastr: ToastrService, private cdRef: ChangeDetectorRef, private sanitizer: DomSanitizer,
    private dashboardService: LLDashboardService,
    private settingsService: SettingsService) {
  }

  toHtml(input): any {
    return input ? new DOMParser().parseFromString(input, 'text/html').documentElement.textContent : '';
  }

  public encodeHtml(str) {
    const buf = [];
    if (str) {
      for (let i = str.length - 1; i >= 0; i--) {
        buf.unshift(['&#', str[i].charCodeAt(), ';'].join(''));
      }

      return buf.join('');
    }
  }

  public decodeHtml(str) {

    if (str) {
      return str.replace(/&#(\d+);/g, function (match, dec) {
        return String.fromCharCode(dec);
      });
    }
  }

  ngAfterViewInit(): void {

    this.sub = this.viewChildren.changes.subscribe((comps: QueryList<NgbNav>) => {
      this.tabset = comps?.first;
      this.cdRef.detectChanges();

      this.activeTab = this.tabset.activeId;
    });

    this.syncGridSpinners();
  }

  tooltipValue(args) {

    let grid;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      switch (this.activeTab) {
        case 'deliveries':
          grid = this.deliveriesGrid;
          break;

        default:
          grid = this.LLForm_grid;
          break;
      }

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltip.cssClass = 'customTooltip';
      this.tooltip.content = this.toBrackets(rowData[(rowInfo.column as any).field]);
    }
  }

  toBrackets(str) {
    const retVal = str.split('&lt;').join('<')
      .split('&gt;').join('>')
      .split('&amp;').join('&');

    return retVal;
  }

  tabChanged(event) {
    this.activeTab = event.nextId;
    const isDeleteAllowedOnTab = this.activeTab !== 'deliveries'
    this.llFormGridRowDeselected();
    this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, isDeleteAllowedOnTab, this.bHasPermission);
    this.selectedLLRevisionId = undefined;
    setTimeout(() => this.syncGridSpinners());
  }

  ngOnInit(): void {

    this.searchForm = this.fb.group({
      searchText: ['', { validators: [Validators.required], updateOn: 'blur' }]
    });

    this.initialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100, 'All'] };
    this.llFormsPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    this.llDeliveriesPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    this.formatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() + ' hh:mm a' };

    // Initialize server-side pagination request (isDraftMode: null = all forms)
    this.llFormsRequest = {
      pageNumber: 1,
      pageSize: 25,
      searchTerm: '',
      sortColumn: 'modifiedDate',
      sortDirection: 'DESC',
      isDraftMode: false,
      columnFilters: {}
    };

    // Initialize server-side pagination request for deliveries
    this.llDeliveriesRequest = {
      pageNumber: 1,
      pageSize: 25,
      searchTerm: '',
      sortColumn: 'ChangeDate',
      sortDirection: 'DESC',
      isDraftMode: false,
      columnFilters: {}
    };

    // Check if user has write permission. If no, disable the "Save", "Save a draft" buttons
    this.checkUserPermission();
    this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, true, this.bHasPermission);

    // Fix for footer spacing issues
    document.getElementsByTagName('footer')[0].classList.add("footer-spacing-fix");

    // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex();

    // Load LL forms and deliveries with server-side pagination
    this.loadLLForms();
    this.loadDeliveries();
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  loadDeliveries() {
    this.loadingDeliveries = true;
    this.setGridLoading(this.deliveriesGrid, true);
    this.dashboardService.getLLMergeDeliveryDashboardPaginated(this.llDeliveriesRequest).subscribe({
      next: (response: any) => {
        const result = response?.data;
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = result?.data ?? [];

        this.llDeliveries_grid_data = { result: data, count: totalRecords };

        // Update needsToPoll based on pragmatic validation status
        const today: Date = new Date();
        const numberOfDaysAgo: Date = new Date(today.getTime() - parseInt(this.settingsService.pragmaticValidatorAutoRefreshDays, 10) * 24 * 60 * 60 * 1000);
        const inProgress = data.filter(item => item.pragmaticTaskStatusCode === 1);
        const hasRecent = inProgress.some(item => new Date(item.pragmaticValidationDate) >= numberOfDaysAgo);
        if (!hasRecent) {
          this.needsToPoll = false;
        }

        this.loadingDeliveries = false;
        this.setGridLoading(this.deliveriesGrid, false);
      },
      error: () => {
        this.loadingDeliveries = false;
        this.setGridLoading(this.deliveriesGrid, false);
      }
    });
  }

  onDeliveriesDataStateChange(state: any): void {
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    if (state.skip !== undefined && state.take !== undefined) {
      this.llDeliveriesRequest.pageNumber = Math.floor(state.skip / state.take) + 1;
      this.llDeliveriesRequest.pageSize = state.take;
    }
    if (state.sorted && state.sorted.length > 0) {
      this.llDeliveriesRequest.sortColumn = state.sorted[0].name;
      this.llDeliveriesRequest.sortDirection = state.sorted[0].direction === 'descending' ? 'DESC' : 'ASC';
    }
    if (state.search && state.search.length > 0) {
      this.llDeliveriesRequest.searchTerm = state.search[0].key || '';
      this.llDeliveriesRequest.pageNumber = 1;
    } else if (state.action?.requestType === 'searching') {
      this.llDeliveriesRequest.searchTerm = state.action.searchString || '';
      this.llDeliveriesRequest.pageNumber = 1;
    }
    if (state.action && (state.action.requestType === 'filtering' || state.action.requestType === 'clearfiltering')) {
      const newFilters: { [key: string]: string } = {};
      if (state.where && state.where.length > 0) {
        state.where.forEach(condition => {
          const preds = condition.predicates ?? [condition];
          preds.forEach(pred => {
            if (pred.field && pred.value !== undefined && pred.value !== null && pred.value !== '') {
              newFilters[pred.field] = String(pred.value);
            }
          });
        });
      }
      this.llDeliveriesRequest.columnFilters = newFilters;
      this.llDeliveriesRequest.pageNumber = 1;
    }
    this.loadDeliveries();
  }

  // Load LL forms with server-side pagination
  loadLLForms() {
    this.loadingLLForms = true;
    this.setGridLoading(this.LLForm_grid, true);
    this.dashboardService.getLLFormsPaginated(this.llFormsRequest).subscribe(
      (response: any) => {
        const result = response?.data;
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = result?.data ?? [];

        this.count_LLForm = totalRecords;

        this.LLForm_grid_data = { result: data, count: totalRecords };
        this.loadingLLForms = false;
        this.setGridLoading(this.LLForm_grid, false);
      },
      error => {
        this.loadingLLForms = false;
        this.setGridLoading(this.LLForm_grid, false);
        this.toastr.error(error.message || 'Failed to load products');
      }
    );
  }

  private syncGridSpinners(): void {
    this.setGridLoading(this.LLForm_grid, this.loadingLLForms);
    this.setGridLoading(this.deliveriesGrid, this.loadingDeliveries);
  }

  private setGridLoading(grid: GridComponent | undefined, isLoading: boolean): void {
    setTimeout(() => {
      if (!grid) {
        return;
      }

      if (isLoading) {
        grid.showSpinner();
      } else {
        grid.hideSpinner();
      }
    });
  }

  // Handle LL forms grid data state changes (paging, sorting, searching, filtering)
  onLLFormsDataStateChange(state: any): void {
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(state);
    this.loadLLForms();
  }

  private updateRequestFromState(state: any): void {
    // Paging
    if (state.skip !== undefined && state.take !== undefined) {
      this.llFormsRequest.pageNumber = Math.floor(state.skip / state.take) + 1;
      this.llFormsRequest.pageSize = state.take;
    }

    // Sorting
    if (state.sorted && state.sorted.length > 0) {
      const sortInfo = state.sorted[0];
      this.llFormsRequest.sortColumn = sortInfo.name;
      this.llFormsRequest.sortDirection = sortInfo.direction === 'descending' ? 'DESC' : 'ASC';
    } else if (state.action && state.action.requestType === 'sorting') {
      if (state.action.columnName) {
        this.llFormsRequest.sortColumn = state.action.columnName;
        this.llFormsRequest.sortDirection = state.action.direction === 'Descending' ? 'DESC' : 'ASC';
      } else {
        this.llFormsRequest.sortColumn = '';
        this.llFormsRequest.sortDirection = 'ASC';
      }
    }

    // Searching
    if (state.search && state.search.length > 0) {
      this.llFormsRequest.searchTerm = state.search[0].key || '';
      this.llFormsRequest.pageNumber = 1;
    } else if (state.action && state.action.requestType === 'searching') {
      this.llFormsRequest.searchTerm = state.action.searchString || '';
      this.llFormsRequest.pageNumber = 1;
    } else if (!state.search || state.search.length === 0) {
      if (this.llFormsRequest.searchTerm) {
        this.llFormsRequest.searchTerm = '';
        this.llFormsRequest.pageNumber = 1;
      }
    }

    // Filtering
    if (state.action && (state.action.requestType === 'filtering' || state.action.requestType === 'clearfiltering')) {
      const newFilters: { [key: string]: string } = {};

      if (state.where && state.where.length > 0) {
        state.where.forEach(condition => {
          if (condition.predicates && condition.predicates.length > 0) {
            condition.predicates.forEach(pred => {
              if (pred.field && pred.value !== undefined && pred.value !== null && pred.value !== '') {
                if (pred.field === 'formVersion') {
                  newFilters[pred.field] = `${pred.value}.0`;
                } else {
                  newFilters[pred.field] = String(pred.value);
                }
              }
            });
          } else if (condition.field && condition.value !== undefined && condition.value !== null && condition.value !== '') {
            if (condition.field === 'formVersion') {
              newFilters[condition.field] = `${condition.value}.0`;
            } else {
              newFilters[condition.field] = String(condition.value);
            }
          }
        });
      }

      this.llFormsRequest.columnFilters = newFilters;
      this.llFormsRequest.pageNumber = 1;
    }
  }

  gridActionComplete(args: any) {
    switch (args?.requestType) {
      case 'paging':
      case 'sorting':
      case 'filtering':
      case 'searching':
      case 'refresh':
        this.restoreSelection();
        break;
    }
  }

  private restoreSelection() {
    if (!this.LLForm_grid || !this.selectedLLFormRevisionId) {
      return;
    }

    const view = this.LLForm_grid.getCurrentViewRecords() as any[];
    let rowIndex = -1;
    if (view && view.length) {
      for (let i = 0; i < view.length; i++) {
        if (view[i] && view[i].llRevisionId === this.selectedLLFormRevisionId) {
          rowIndex = i;
          break;
        }
      }
    }

    if (rowIndex >= 0) {
      this.LLForm_grid.selectRow(rowIndex);
    }
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  downloadFile(filePath: string, fileName: string) {

    this.dashboardService.downloadFile(filePath, fileName).subscribe(response => {
      const URL = window.URL || (<any>window).webkitURL;
      const downloadUrl = URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
    },
      error => {
        this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
      }
    );
  }

  // Change grid filter operator from "startwith" to "contains" for columns that are string
  public begin(args: any): void {
    if (args.requestType === 'filtering') {
      if (args.columns) {
        args.columns.forEach(col => {
          switch (args.currentFilteringColumn) {
            case 'formVersion':
              args.currentFilterObject.value = `${args.currentFilterObject.value}.0`;
              break;
            case 'modifiedDate':
              break;
            default:
              col.operator = 'contains';       // set the "contains" operator for the column
              break;
          }
        });
      }
    }
  }

  // Display detail of a single LL form
  viewFormOrGoToDashboard(llRevisionId: string, version: string) {
    if (llRevisionId) {
      const url = parseInt(version, 10) >= 1 ? '/products/dashboard' : '/products/ll-form';
      this.router.navigate([url, llRevisionId]);
    }
  }

  // Check if user has write permission. If no, disable the "Save", "Save a draft" buttons
  checkUserPermission() {
    if (this.authorizationService.hasPermission('SSDP.CanWriteLLForm')) {
      this.bHasPermission = true;
    }
    if (this.authorizationService.hasPermission('SSDP.CanDeleteLLForm')) {
      this.hasDeletePermission = true;
    }
  }

  createCustomToolbarItems(deletePermission: boolean, isDeleteAllowedOnTab: boolean, writePermission: boolean) {
    let toolbarItems = [];

    if (this.activeTab === 'deliveries' && writePermission) {
      toolbarItems.push(
        {
          text: 'Send to FDA',
          tooltipText: 'Send to FDA',
          prefixIcon: 'fas fa-paper-plane',
          id: 'tool-bar-send'
        }
      );
    }

    if (writePermission) {
      toolbarItems.push(
        {
          text: 'Create New Product',
          tooltipText: 'Add a new Product to this list',
          prefixIcon: 'fas fa-plus',
          id: 'create-form-btn'
        }
      );
    }

    if (deletePermission && isDeleteAllowedOnTab) {
      toolbarItems.push(
        {
          text: 'Delete Product',
          tooltipText: 'Delete the selected SPL',
          prefixIcon: 'fas fa-trash',
          id: 'tool-bar-delete'
        }
      );
    }

    toolbarItems.push('Search');

    return toolbarItems;
  }

  getStatusClass(status) {
    let cssClass;

    if (status) {

      switch (+status) {

        case 3:

          cssClass = 'text-success';
          break;

        case 4:

          cssClass = 'text-danger';
          break;
      }
    }
    return cssClass;
  }

  getStatusIcon(status) {
    let cssClass;

    if (status) {

      switch (status) {

        case 1:
        case 2:
          cssClass = 'fas fa-spinner text-info';
          break;
        case 3:

          cssClass = 'fas fa-check-circle text-success';
          break;

        case 4:

          cssClass = 'fas fa-window-close text-danger';
          break;
      }
    }
    return cssClass;
  }

  customizeCell(args: QueryCellInfoEventArgs) {
    if (args.column.field === 'statusDescription') {
      if (args.data['taskStatusId'] === 1 || args.data['taskStatusId'] === 2) {
        (args.cell as any).style.color = '#17a2b8';
      } else if (args.data['taskStatusId'] === 3) {
        (args.cell as any).style.color = '#77b800';
      } else if (args.data['taskStatusId'] === 4) {
        (args.cell as any).style.color = '#a94442';
      }
    }
  }

  llFormGridRowSelected(e: any) {
    this.selectedLLRevisionId = e.data.llRevisionId;
    this.selectedLLRevisionDraftMode = e.data.isDraftMode;
    this.selectedLLRevisionProductType = e.data.productType;
    this.selectedLLRevisionVersion = e.data.formVersion;
    // Track for selection persistence
    this.selectedLLFormRevisionId = e.data.llRevisionId;
  }

  llFormGridRowDeselected() {
    this.selectedLLRevisionId = undefined;
    this.selectedLLRevisionDraftMode = undefined;
    this.selectedLLOrderId = undefined;
    this.selectedLLRevisionProductType = undefined;
    this.selectedLLRevisionFileName = undefined;
    this.selectedLLRevisionVersion = undefined;
    this.selectedLLFormRevisionId = null;
  }

  llDeliveriesGridRowSelected(e: any) {
    this.selectedLLRevisionId = e.data.formRevisionId;
    this.selectedLLOrderId = e.data.orderId;
    this.selectedLLRevisionProductType = e.data.productType;
    this.selectedLLRevisionFileName = e.data.fileName;
    this.selectedLLRevisionVersion = e.data.formVersion;
  }

  // convenience getter for easy access to form fields
  get f() { return this.searchForm.controls; }

  onSubmitionToFDA(isSuccessfulySubmitted: boolean) {
    if (isSuccessfulySubmitted) {
      if (this.deliveriesGrid) {
        this.deliveriesGrid.clearSelection();
      }
    }
  }

  toolbarClick(args: ClickEventArgs): void {
    if (args.item.id === 'create-form-btn' && this.bHasPermission) {
      this.router.navigate(["/create-ll-form"]);
    }
    else if (args.item.id === 'tool-bar-delete') {
      if (this.LLForm_grid && this.selectedLLRevisionId) {
        this.deleteButton.revisionId = this.selectedLLRevisionId.toString();
        this.deleteButton.openDeleteModal();
      }
      else if (this.draftsGrid && this.selectedLLRevisionId) {
        this.deleteButtonDrafts.revisionId = this.selectedLLRevisionId.toString();
        this.deleteButtonDrafts.openDeleteModal();
      }
    }
    else if (this.deliveriesGrid && this.selectedLLRevisionFileName && args.item.id === 'tool-bar-send') {
      this.sendToFDAButton.showEsignatureModal();
    }
  }
}
